import { forwardRef, type PropsWithChildren } from 'react'
import { Button, type ModalProps } from 'react-bootstrap'
import { BsQuestionCircleFill, BsExclamationTriangleFill } from 'react-icons/bs'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import MaModal, { type MaModalRef } from './MaModal'
import styles from '../assets/scss/MaConfirm.module.scss'
import ButtonLoadingSpinner from './ButtonLoadingSpinner'

interface MaConfirmProps extends ModalProps, PropsWithChildren {
  readonly variant?: 'default' | 'question' | 'warning'
  readonly confirmLabel?: string
  readonly closeLabel?: string
  readonly disabled?: boolean
  readonly onConfirm: () => void
  readonly onClose: () => void
  readonly isLoading: boolean
  readonly footer?: Element
}

const MaConfirm = forwardRef<MaModalRef, MaConfirmProps>(
  (
    {
      className,
      confirmLabel,
      closeLabel,
      disabled = false,
      variant = 'default',
      onConfirm,
      onClose,
      isLoading = false,
      footer,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation()

    return (
      <MaModal
        {...props}
        ref={ref}
        onClose={onClose}
        className={classNames(
          styles['ma-confirm'],
          {
            [styles.question]: variant === 'question',
            [styles.warning]: variant === 'warning'
          },
          className as string
        )}
        {...(variant === 'question' && {
          header: (
            <div className={styles.header}>
              <BsQuestionCircleFill size={24} />
              <h4>{props.title}</h4>
            </div>
          )
        })}
        {...(variant === 'warning' && {
          header: (
            <div className={styles.header}>
              <BsExclamationTriangleFill size={24} />
              <h4>{props.title}</h4>
            </div>
          )
        })}
        footer={
          footer || (
            <div className={styles.buttons}>
              <Button
                variant="outline-danger"
                onClick={onClose}
                disabled={isLoading}
                className={styles.close}
              >
                {closeLabel || t('common.button.labels.close')}
              </Button>

              <Button
                onClick={onConfirm}
                disabled={disabled}
                className={styles.confirm}
              >
                {confirmLabel || t('common.button.labels.confirm')}
                {isLoading && <ButtonLoadingSpinner />}
              </Button>
            </div>
          )
        }
      />
    )
  }
)

MaConfirm.displayName = 'MaConfirm'

export default MaConfirm
